import { TEventTypes } from '../../generated/graphql';

export const ONE_OFF_EVENT_TYPE_OPTIONS = [
  { label: 'Baby Gift', value: TEventTypes.BabyGift },
  { label: 'Wedding/Engagement', value: TEventTypes.Wedding },
  { label: 'Care Package', value: TEventTypes.CarePackage },
  { label: 'Custom Bricks', value: TEventTypes.CustomBricks }
];

export const ONE_OFF_EVENT_TYPES = ONE_OFF_EVENT_TYPE_OPTIONS.map(({ value }) => value);

export type TBrickColor = {
  background: string;
  label: string;
  text: string;
  value: string;
};

// Swatches approximate the supplied brick reference photo; lighting and metallic finish affect appearance.
// Keep existing stored values (blue, red, green, gold, silver) while using the catalog labels.
export const BRICK_COLOR_CATALOG: Record<string, TBrickColor> = {
  gold: { background: '#88722f', label: 'Metallic Gold', text: '#ffffff', value: 'gold' },
  silver: { background: '#77716d', label: 'Metallic Silver', text: '#ffffff', value: 'silver' },
  redBrown: { background: '#54200d', label: 'Red Brown', text: '#ffffff', value: 'redBrown' },
  darkTan: { background: '#806b3e', label: 'Dark Tan', text: '#ffffff', value: 'darkTan' },
  lightTan: { background: '#b9a76c', label: 'Light Tan', text: '#111111', value: 'lightTan' },
  coral: { background: '#e65d36', label: 'Coral', text: '#ffffff', value: 'coral' },
  pink: { background: '#bc83a0', label: 'Pink', text: '#ffffff', value: 'pink' },
  newDarkRed: { background: '#650908', label: 'New Dark Red', text: '#ffffff', value: 'newDarkRed' },
  red: { background: '#bc1709', label: 'Bright Red', text: '#ffffff', value: 'red' },
  brightOrange: { background: '#ed7a08', label: 'Bright Orange', text: '#ffffff', value: 'brightOrange' },
  brightLightOrange: { background: '#e7a91a', label: 'Bright Light Orange', text: '#111111', value: 'brightLightOrange' },
  yellow: { background: '#dec12c', label: 'Yellow', text: '#111111', value: 'yellow' },
  lime: { background: '#8ea92d', label: 'Lime', text: '#111111', value: 'lime' },
  brightGreen: { background: '#16954d', label: 'Bright Green', text: '#ffffff', value: 'brightGreen' },
  green: { background: '#075e3d', label: 'Dark Green', text: '#ffffff', value: 'green' },
  earthGreen: { background: '#102d24', label: 'Earth Green', text: '#ffffff', value: 'earthGreen' },
  brightBluegreen: { background: '#08768a', label: 'Bright Bluegreen', text: '#ffffff', value: 'brightBluegreen' },
  mediumAzure: { background: '#459eb8', label: 'Medium Azure', text: '#ffffff', value: 'mediumAzure' },
  darkAzure: { background: '#147da8', label: 'Dark Azure', text: '#ffffff', value: 'darkAzure' },
  mediumBlue: { background: '#588fa7', label: 'Medium Blue', text: '#ffffff', value: 'mediumBlue' },
  blue: { background: '#073799', label: 'Bright Blue', text: '#ffffff', value: 'blue' },
  earthBlue: { background: '#142037', label: 'Earth Blue', text: '#ffffff', value: 'earthBlue' },
  purple: { background: '#39205c', label: 'Purple', text: '#ffffff', value: 'purple' },
  lavender: { background: '#9461a2', label: 'Lavender', text: '#ffffff', value: 'lavender' },
  black: { background: '#181814', label: 'Black', text: '#ffffff', value: 'black' },
  darkGray: { background: '#555653', label: 'Dark Gray', text: '#ffffff', value: 'darkGray' },
  lightGray: { background: '#92958f', label: 'Light Gray', text: '#111111', value: 'lightGray' },
  white: { background: '#e5e2d9', label: 'White', text: '#111111', value: 'white' }
};

// Match the photographed stacks from top to bottom. API validation mirrors this catalog
// in cs-api/functions/events/createOneOffEvent.ts. The photo does not specify 2x2 colors.
export const BRICK_COLOR_OPTIONS_BY_SIZE = {
  '2x2': ['black', 'blue', 'green', 'red', 'white', 'yellow'],
  '2x4': [
    'gold',
    'silver',
    'redBrown',
    'darkTan',
    'lightTan',
    'pink',
    'newDarkRed',
    'red',
    'brightOrange',
    'brightLightOrange',
    'yellow',
    'lime',
    'green',
    'earthGreen',
    'brightBluegreen',
    'mediumAzure',
    'darkAzure',
    'blue',
    'earthBlue',
    'purple',
    'lavender',
    'black',
    'darkGray',
    'lightGray',
    'white'
  ],
  '2x6': [
    'gold',
    'silver',
    'coral',
    'pink',
    'newDarkRed',
    'red',
    'brightOrange',
    'yellow',
    'lime',
    'brightGreen',
    'green',
    'earthGreen',
    'brightBluegreen',
    'mediumAzure',
    'blue',
    'earthBlue',
    'purple',
    'black',
    'darkGray',
    'lightGray',
    'white'
  ],
  '2x8': ['lightTan', 'pink', 'red', 'yellow', 'mediumBlue', 'blue', 'earthBlue', 'lavender', 'black', 'darkGray', 'lightGray', 'white']
} as const;

export type TBrickSize = keyof typeof BRICK_COLOR_OPTIONS_BY_SIZE;

export type TBrickSizeConfiguration = {
  heightMm: number;
  label: string;
  recommendedCharacters: number;
  value: TBrickSize;
  widthMm: number;
};

// Dimensions come from the first Jig cell in PrintCreator's matching SVG template.
export const BRICK_SIZE_CONFIG: Record<TBrickSize, TBrickSizeConfiguration> = {
  '2x2': { heightMm: 9.4927311, label: '2x2', recommendedCharacters: 8, value: '2x2', widthMm: 15.893217 },
  '2x4': { heightMm: 9.4597054, label: '2x4', recommendedCharacters: 18, value: '2x4', widthMm: 31.907318 },
  '2x6': { heightMm: 9.4464703, label: '2x6', recommendedCharacters: 28, value: '2x6', widthMm: 47.872135 },
  '2x8': { heightMm: 9.4789495, label: '2x8', recommendedCharacters: 38, value: '2x8', widthMm: 63.783276 }
};

export const BRICK_SIZE_OPTIONS = Object.values(BRICK_SIZE_CONFIG).map(({ label, value }) => ({ label, value }));

export type TOneOffEventFormValues = {
  brickColor: string | null;
  brickSize: TBrickSize;
  brickText: string;
  brickQuantity: number;
  customType?: null | string;
  employeeIds: string[];
  eventDate: Date | string | null;
  notes?: null | string;
  type: null | TEventTypes;
};

const PRINT_MARGIN_MM = 1.5;
const PIXELS_PER_MM = 96 / 25.4;
const POINT_TO_PIXEL = 96 / 72;
const FONT_SIZE_START = 18;
const FONT_SIZE_MIN = 6;
const FONT_SIZE_STEP = 0.5;
export const BRICK_PREVIEW_FONT_FAMILY = 'Avenir, "Avenir Next", "Helvetica Neue", Arial, sans-serif';

export const millimetersToPixels = (value: number) => value * PIXELS_PER_MM;
export const pointsToPixels = (value: number) => value * POINT_TO_PIXEL;

let measuringContext: CanvasRenderingContext2D | null | undefined;

const getMeasuringContext = () => {
  if (measuringContext !== undefined) return measuringContext;
  if (typeof document === 'undefined') return null;

  measuringContext = document.createElement('canvas').getContext('2d');
  return measuringContext;
};

export const normalizeBrickText = (value?: null | string) => {
  return (value || '').trim().replace(/[\t\u00a0\u1680\u2000-\u200a\u202f\u205f\u3000]/g, ' ');
};

export type TBrickTextFit = {
  fits: boolean;
  fontSize: number;
  normalizedText: string;
};

export const fitBrickText = (value: string, brickSize: TBrickSize): TBrickTextFit => {
  const normalizedText = normalizeBrickText(value);
  const config = BRICK_SIZE_CONFIG[brickSize];
  const maxWidth = millimetersToPixels(config.widthMm - PRINT_MARGIN_MM * 2);
  const maxHeight = millimetersToPixels(config.heightMm - PRINT_MARGIN_MM * 2);
  const context = getMeasuringContext();

  const measureText = (fontSize: number) => {
    const fontSizeInPixels = pointsToPixels(fontSize);

    if (context) {
      context.font = `300 ${fontSizeInPixels}px ${BRICK_PREVIEW_FONT_FAMILY}`;
      return context.measureText(normalizedText).width;
    }

    return normalizedText.length * fontSizeInPixels * 0.52;
  };

  for (let fontSize = FONT_SIZE_START; fontSize >= FONT_SIZE_MIN; fontSize -= FONT_SIZE_STEP) {
    const measuredWidth = measureText(fontSize);
    const fontHeight = pointsToPixels(fontSize);

    if (measuredWidth <= maxWidth && fontHeight <= maxHeight) return { fits: true, fontSize, normalizedText };
  }

  const minimumMeasuredWidth = measureText(FONT_SIZE_MIN);

  return {
    fits: minimumMeasuredWidth <= maxWidth && pointsToPixels(FONT_SIZE_MIN) <= maxHeight,
    fontSize: FONT_SIZE_MIN,
    normalizedText
  };
};

export const getBrickColorOptions = (brickSize: TBrickSize) => {
  return BRICK_COLOR_OPTIONS_BY_SIZE[brickSize].map((color) => BRICK_COLOR_CATALOG[color]);
};

export const getOneOffEventTypeLabel = (type: TEventTypes, customType?: null | string) => {
  if ((type === TEventTypes.Other || type === TEventTypes.CustomBricks) && customType) return customType;

  return ONE_OFF_EVENT_TYPE_OPTIONS.find(({ value }) => value === type)?.label || type;
};
